import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, FileText, Eye, Star, Pencil, Trash2, Upload, X, CheckCircle,
  AlertTriangle, Info,
} from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';

/**
 * Storage Agreements — facility-scoped lease template catalog.
 *
 * Maps 1:1 to backend/routers/lease_templates.py. Lets the operator
 * upload DOCX templates with Jinja2-style merge tags, preview them with
 * sample data, pin one as the facility default, and edit metadata.
 *
 * Wired from Settings -> Documents -> "Lease Template (Storage Agreements)".
 */

// Mirrors models/unit.py UnitCategory enum. Used by the multi-select
// chips on the upload + edit modals; same labels as UnitTypesPanel.
const CATEGORY_OPTIONS = [
  { value: 'enclosed', label: 'Enclosed' },
  { value: 'outdoor_parking', label: 'Outdoor Parking' },
  { value: 'covered_canopy', label: 'Covered Canopy' },
  { value: 'climate_controlled', label: 'Climate Controlled' },
];

// Inlined from the lease merge-tag reference
// so operators don't need to leave the page to look these up.
const MERGE_TAG_GROUPS = [
  {
    section: 'Customer',
    tags: [
      ['{{customer.full_name}}', "Customer's legal name on file"],
      ['{{customer.email}}', 'Email address'],
      ['{{customer.phone}}', 'Phone number'],
      ['{{customer.address}}', 'Mailing address (single-line)'],
      ['{{customer.gov_id_last4}}', 'Last 4 of gov ID (if on file)'],
    ],
  },
  {
    section: 'Unit',
    tags: [
      ['{{unit.unit_number}}', 'Unit number (e.g. "A-12", "204")'],
      ['{{unit_type.name}}', 'Unit type catalog name (e.g. "10x10")'],
      ['{{unit_type.width}}', 'Advertised width (numeric)'],
      ['{{unit_type.length}}', 'Advertised length (numeric)'],
    ],
  },
  {
    section: 'Rental terms',
    tags: [
      ['{{rental.start_date}}', 'Lease start date (ISO yyyy-mm-dd)'],
      ['{{rental.monthly_rate}}', 'Monthly rent (dollars, no $ sign)'],
      ['{{rental.security_deposit}}', 'Security deposit (numeric)'],
    ],
  },
  {
    section: 'Facility',
    tags: [
      ['{{facility.name}}', 'Facility display name'],
      ['{{facility.address}}', 'Street address'],
      ['{{facility.city}}', 'City'],
      ['{{facility.state}}', 'State (2-letter)'],
      ['{{facility.zip}}', 'ZIP code'],
      ['{{facility.phone}}', 'Facility contact phone'],
    ],
  },
  {
    section: 'Meta',
    tags: [
      ['{{today}}', 'Render-time date (ISO yyyy-mm-dd)'],
      ['{{lease_id}}', 'mySMS360 lease document _id'],
    ],
  },
];

const base = (facilityId) => `/api/facilities/${facilityId}/lease-templates`;

const StorageAgreements = () => {
  const { facilityId } = useFacility();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [previewTpl, setPreviewTpl] = useState(null);
  const [editingTpl, setEditingTpl] = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.get(`${base(facilityId)}/`, {
        params: { include_inactive: true },
      });
      setTemplates(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load templates');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    load();
  }, [load]);

  const setDefault = async (t) => {
    if (!window.confirm(`Set "${t.name}" as the facility default lease template?`)) return;
    try {
      await apiClient.post(`${base(facilityId)}/${t._id}/set-default`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not set default');
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      const r = await apiClient.delete(`${base(facilityId)}/${t._id}`);
      if (r.data?.soft_deleted) {
        alert(r.data.message || 'Template marked inactive (still referenced).');
      }
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (!facilityId) {
    return (
      <div className="text-[13px] text-slate-500 py-10 text-center">
        Select a facility to manage lease templates.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Storage Agreements</h1>
          <p className="text-[13px] text-slate-400 mt-0.5">
            Upload DOCX lease templates with merge tags. Pin one as the facility
            default; unit types can override with their own template.
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Upload Template
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200/80 px-3 py-2.5 text-[13px] text-red-600">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left font-medium px-4 py-2.5">Name</th>
                <th className="text-left font-medium px-4 py-2.5">Description</th>
                <th className="text-left font-medium px-4 py-2.5">Applicable categories</th>
                <th className="text-left font-medium px-4 py-2.5">Status</th>
                <th className="text-right font-medium px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                    Loading…
                  </td>
                </tr>
              ) : templates.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                    <FileText className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    No lease templates yet. Click "Upload Template" to add one.
                  </td>
                </tr>
              ) : (
                templates.map((t) => (
                  <tr key={t._id} className={!t.is_active ? 'opacity-60' : ''}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900 flex items-center gap-2">
                        {t.name}
                        {t.is_default && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200/60">
                            <Star className="h-3 w-3" /> Default
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 max-w-md">
                      <div className="line-clamp-2">{t.description || '—'}</div>
                    </td>
                    <td className="px-4 py-3">
                      {(t.applicable_categories || []).length === 0 ? (
                        <span className="text-slate-400">Any</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(t.applicable_categories || []).map((c) => (
                            <span
                              key={c}
                              className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-600"
                            >
                              {CATEGORY_OPTIONS.find((o) => o.value === c)?.label || c}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {t.is_active === false ? (
                        <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-slate-100 text-slate-500">
                          Inactive
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-50 text-emerald-600">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setPreviewTpl(t)}
                          className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-blue-600"
                          title="Preview"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Preview
                        </button>
                        {!t.is_default && t.is_active !== false && (
                          <button
                            onClick={() => setDefault(t)}
                            className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-amber-600"
                            title="Set as default"
                          >
                            <Star className="h-3.5 w-3.5" />
                            Set default
                          </button>
                        )}
                        <button
                          onClick={() => setEditingTpl(t)}
                          className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => remove(t)}
                          className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showUpload && (
        <UploadTemplateModal
          facilityId={facilityId}
          onClose={() => setShowUpload(false)}
          onSaved={() => { setShowUpload(false); load(); }}
        />
      )}
      {previewTpl && (
        <PreviewModal
          facilityId={facilityId}
          template={previewTpl}
          onClose={() => setPreviewTpl(null)}
        />
      )}
      {editingTpl && (
        <EditDrawer
          facilityId={facilityId}
          template={editingTpl}
          onClose={() => setEditingTpl(null)}
          onSaved={() => { setEditingTpl(null); load(); }}
        />
      )}
    </div>
  );
};

// ── Upload modal ──────────────────────────────────────────────────────

export const UploadTemplateModal = ({ facilityId, onClose, onSaved }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState([]);
  const [isDefault, setIsDefault] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const onPickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.docx')) {
      setError('Lease templates must be .docx files.');
      return;
    }
    setError(null);
    setFile(f);
    if (!name) {
      // Strip extension to seed the name input.
      setName(f.name.replace(/\.docx$/i, ''));
    }
  };

  const toggleCategory = (val) => {
    setCategories((prev) =>
      prev.includes(val) ? prev.filter((c) => c !== val) : [...prev, val]
    );
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError(null);
    if (!file) { setError('Pick a DOCX file first.'); return; }
    if (!name.trim()) { setError('Name is required.'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', name.trim());
    if (description.trim()) fd.append('description', description.trim());
    if (categories.length) fd.append('applicable_categories', categories.join(','));
    fd.append('is_default', isDefault ? 'true' : 'false');
    setSubmitting(true);
    try {
      await apiClient.post(`${base(facilityId)}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Upload lease template" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {/* Drag-drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            onPickFile(e.dataTransfer.files?.[0]);
          }}
          className={`rounded-lg border-2 border-dashed transition-colors px-4 py-6 text-center cursor-pointer ${
            dragOver
              ? 'border-blue-400 bg-blue-50'
              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
          }`}
          onClick={() => document.getElementById('lease-tpl-file').click()}
        >
          <input
            id="lease-tpl-file"
            type="file"
            accept=".docx"
            className="hidden"
            onChange={(e) => onPickFile(e.target.files?.[0])}
          />
          <Upload className="h-6 w-6 text-slate-400 mx-auto mb-2" />
          {file ? (
            <p className="text-[13px] text-slate-700">
              <span className="font-medium">{file.name}</span>
              <span className="text-slate-400"> ({Math.round(file.size / 1024)} KB)</span>
            </p>
          ) : (
            <p className="text-[13px] text-slate-500">
              Drop a DOCX here, or <span className="text-blue-600 font-medium">browse</span>
            </p>
          )}
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Name *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your Storage - Unit Lease"
            className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px]"
          />
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Optional — what's this template for?"
            className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px]"
          />
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Applicable categories</label>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_OPTIONS.map((c) => {
              const on = categories.includes(c.value);
              return (
                <button
                  type="button"
                  key={c.value}
                  onClick={() => toggleCategory(c.value)}
                  className={`px-2.5 py-1 rounded-full text-[12px] border transition-colors ${
                    on
                      ? 'bg-blue-50 border-blue-200 text-blue-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            Advisory only — the cascade still falls through to the facility default.
          </p>
        </div>

        <label className="flex items-center gap-2 text-[13px] text-slate-700">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="rounded border-slate-300"
          />
          Set as facility default
        </label>

        <MergeTagHelp />

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-[13px] text-red-600">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ── Edit drawer ───────────────────────────────────────────────────────

const EditDrawer = ({ facilityId, template, onClose, onSaved }) => {
  const [name, setName] = useState(template.name || '');
  const [description, setDescription] = useState(template.description || '');
  const [categories, setCategories] = useState(template.applicable_categories || []);
  const [isActive, setIsActive] = useState(template.is_active !== false);
  const [replaceFile, setReplaceFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const toggleCategory = (val) => {
    setCategories((prev) =>
      prev.includes(val) ? prev.filter((c) => c !== val) : [...prev, val]
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.put(`${base(facilityId)}/${template._id}`, {
        name: name.trim() || null,
        description: description.trim() || null,
        applicable_categories: categories,
        is_active: isActive,
      });
      // If the operator chose a new DOCX, replace the file IN PLACE on this
      // same row (no new duplicate-named row). Backend swaps the stored file,
      // bumps version, and deletes the old one.
      if (replaceFile) {
        const fd = new FormData();
        fd.append('file', replaceFile);
        await apiClient.post(
          `${base(facilityId)}/${template._id}/replace-file`, fd,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
      }
      onSaved();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Edit template: ${template.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Name *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px]"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px]"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Applicable categories</label>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_OPTIONS.map((c) => {
              const on = categories.includes(c.value);
              return (
                <button
                  type="button"
                  key={c.value}
                  onClick={() => toggleCategory(c.value)}
                  className={`px-2.5 py-1 rounded-full text-[12px] border transition-colors ${
                    on
                      ? 'bg-blue-50 border-blue-200 text-blue-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
        <label className="flex items-center gap-2 text-[13px] text-slate-700">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="rounded border-slate-300"
          />
          Active
        </label>

        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">
            Replace DOCX file (optional)
          </label>
          <input
            type="file"
            accept=".docx"
            onChange={(e) => setReplaceFile(e.target.files?.[0] || null)}
            className="w-full text-[13px] text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 file:text-[12px] hover:file:bg-blue-100"
          />
          <p className="mt-1 text-[11px] text-slate-400 flex items-start gap-1">
            <Info className="h-3.5 w-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
            <span>
              Swaps the file on THIS template in place — keeps the same row,
              name, and settings. No duplicate rows. Leave empty to keep the
              current file.
            </span>
          </p>
          {replaceFile && (
            <p className="mt-1 text-[12px] text-emerald-700">
              New file: {replaceFile.name}
            </p>
          )}
        </div>

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-[13px] text-red-600">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ── Preview modal ─────────────────────────────────────────────────────

const PreviewModal = ({ facilityId, template, onClose }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    let revoke = null;
    (async () => {
      try {
        const r = await apiClient.get(
          `${base(facilityId)}/${template._id}/preview`,
          { responseType: 'blob' },
        );
        if (!active) return;
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setPdfUrl(url);
      } catch (e) {
        if (active) setError(e?.response?.data?.detail || 'Preview failed');
      }
    })();
    return () => {
      active = false;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [facilityId, template._id]);

  return (
    <Modal title={`Preview: ${template.name}`} onClose={onClose} wide>
      {error ? (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-[13px] text-red-600">
          <AlertTriangle className="h-4 w-4 inline mr-1" />
          {error}
        </div>
      ) : pdfUrl ? (
        <iframe
          title="Lease preview"
          src={pdfUrl}
          className="w-full h-[70vh] rounded-md border border-slate-200"
        />
      ) : (
        <div className="text-[13px] text-slate-400 py-10 text-center">
          Rendering preview with sample data…
        </div>
      )}
      <p className="text-[11px] text-slate-400 mt-2">
        Sample data is substituted into the merge tags so you can confirm the
        template renders correctly before wiring it to live rentals.
      </p>
    </Modal>
  );
};

// ── Merge tag help (inline panel; matches docs file) ──────────────────

const MergeTagHelp = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-slate-200/80 bg-slate-50/50">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-3 py-2 flex items-center justify-between text-left"
      >
        <span className="text-[13px] font-medium text-slate-700">
          Supported merge tags
        </span>
        <span className="text-[12px] text-blue-600">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          <p className="text-[12px] text-slate-500">
            Paste these tags inline anywhere in your DOCX. The renderer keeps
            the surrounding formatting intact.
          </p>
          {MERGE_TAG_GROUPS.map((g) => (
            <div key={g.section}>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                {g.section}
              </div>
              <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
                {g.tags.map(([tag, meaning]) => (
                  <div
                    key={tag}
                    className="px-2.5 py-1.5 flex items-center justify-between text-[12px]"
                  >
                    <code className="font-mono text-slate-700">{tag}</code>
                    <span className="text-slate-500">{meaning}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-[12px] text-emerald-700 flex items-center gap-1">
            <CheckCircle className="h-3.5 w-3.5" />
            After uploading, hit Preview to QA the substitution.
          </p>
        </div>
      )}
    </div>
  );
};

// ── Generic modal shell ───────────────────────────────────────────────

const Modal = ({ title, children, onClose, wide }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-6">
    <div
      className={`bg-white rounded-xl shadow-xl w-full ${
        wide ? 'max-w-5xl' : 'max-w-lg'
      } max-h-[90vh] overflow-y-auto`}
    >
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
        <h2 className="text-[14px] font-semibold text-slate-900">{title}</h2>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  </div>
);

// Standalone helper used by frontend tests — see __tests__/StorageAgreements.test.js.
// Exposed so the gate logic stays a single source of truth between the modal
// JSX above and the assertion-only smoke suite.
export function canSubmitUpload({ file, name, submitting }) {
  if (submitting) return false;
  if (!file) return false;
  if (!name || !name.trim()) return false;
  return true;
}

export { CATEGORY_OPTIONS };
export default StorageAgreements;
